import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { FiArrowLeft, FiGithub, FiExternalLink } from 'react-icons/fi';
import './CaseStudy.css';

const CaseStudy = () => {
  // Interactive Simulator State
  const [appliance, setAppliance] = useState('refrigerator'); // 'refrigerator' | 'fan' | 'ac'
  const [age, setAge] = useState(6); // years
  const [hours, setHours] = useState(14); // hours/day

  const baseWattage = appliance === 'refrigerator' ? 240 : appliance === 'ac' ? 1400 : 70;
  const isLinear = appliance === 'fan';

  // Calculations
  // Static physics ignores aging degradation entirely
  const staticKwh = ((baseWattage * hours * 30) / 1000).toFixed(1);

  // Pure AI fails on linear loads (hallucinates variance), underestimates degradation on non-linear
  const pureAiKwh = isLinear
    ? (parseFloat(staticKwh) * (0.69 + (age % 3) * 0.16)).toFixed(1)
    : (parseFloat(staticKwh) * 1.04).toFixed(1);

  // SmartWatt Hybrid: Exact physics on linear loads (0% error), accurate degradation on non-linear (+2.2%/year)
  const agingFactor = isLinear ? 1.0 : 1.0 + (age * 0.024);
  const hybridKwh = ((baseWattage * hours * 30 * agingFactor) / 1000).toFixed(1);

  return (
    <div className="case-study-whitepaper">
      {/* Top Navigation */}
      <nav className="cs-nav-header">
        <div className="cs-nav-container">
          <Link to="/" className="cs-back-btn">
            <FiArrowLeft size={16} />
            <span>Return to Portfolio</span>
          </Link>
          <a
            href="https://github.com/JishnuPG-tech/SmartWatt"
            target="_blank"
            rel="noopener noreferrer"
            className="cs-git-pill"
          >
            <FiGithub size={15} />
            <span>Repository</span>
          </a>
        </div>
      </nav>

      {/* Whitepaper Masthead */}
      <header className="cs-masthead">
        <div className="cs-container">
          <div className="cs-eyebrow">Research Monograph • Physics-Informed ML</div>
          <h1 className="cs-headline">
            SmartWatt AI: Solving the <span className="serif-em">Linear Load Paradox</span> in Residential Energy
          </h1>

          <div className="cs-meta-row">
            <div className="cs-author-box">
              <span className="cs-author-name">Jishnu P G</span>
              <span className="cs-author-role">Project Head & Systems Developer</span>
            </div>
            <span className="cs-meta-sep">•</span>
            <span className="cs-date">2025–2026 Production Release</span>
            <span className="cs-meta-sep">•</span>
            <span className="cs-status-tag">Empirically Verified</span>
          </div>

          {/* Abstract Block */}
          <div className="cs-abstract-card">
            <span className="abstract-label">Abstract</span>
            <p className="abstract-text">
              Residential energy estimation algorithms suffer from a fundamental duality. Pure data-driven
              machine learning models fail on static deterministic loads (fans, incandescent fixtures) due
              to lack of temporal feature variance, achieving sub-31% precision in baseline benchmarks.
              Conversely, traditional physics-based formulas assume ideal theoretical efficiency, missing
              18–22% empirical power spread caused by mechanical wear, thermodynamic cycling, and compressor
              aging. We propose <strong>SmartWatt AI</strong>: a dual-inference architecture that dynamically
              enforces physics constraints on linear appliances while utilizing multi-output neural networks
              to capture non-linear degradation in complex inductive loads.
            </p>
          </div>
        </div>
      </header>

      {/* Main Content Body */}
      <main className="cs-main-body">
        <div className="cs-container">
          {/* Section 1: The Dilemma */}
          <section className="cs-section">
            <span className="cs-sec-num">01 / The Mathematical Dilemma</span>
            <h2 className="cs-sec-title">Why Single-Model Estimators Fail</h2>

            <div className="cs-dual-grid">
              <div className="cs-critique-box warning">
                <span className="critique-tag">Deficiency A</span>
                <h3 className="critique-title">Pure Neural Network Hallucination</h3>
                <p className="critique-p">
                  When pure deep learning models are exposed to linear electrical loads (like ceiling fans or
                  fixed-wattage LED banks), the absence of variance forces the optimizer to fit non-existent
                  patterns. Benchmarks showed pure ML models degrading to <strong>31% accuracy</strong> on deterministic
                  circuits.
                </p>
              </div>

              <div className="cs-critique-box muted">
                <span className="critique-tag">Deficiency B</span>
                <h3 className="critique-title">Static Physics Calculator Blindness</h3>
                <p className="critique-p">
                  Standard rule-based energy calculators calculate wattage using nominal nameplate ratings:{' '}
                  <code>kWh = (P × t) / 1000</code>. This completely ignores that a 6-year-old refrigerator
                  consumes 18–24% more energy due to seal degradation and compressor hysteresis.
                </p>
              </div>
            </div>
          </section>

          {/* Section 2: Interactive Empirical Simulator */}
          <section className="cs-section">
            <span className="cs-sec-num">02 / Empirical Simulation</span>
            <h2 className="cs-sec-title">Interactive Dual-Inference Simulator</h2>
            <p className="cs-sec-dek">
              Adjust appliance age and operating duration to witness how SmartWatt Hybrid recovers linear load
              accuracy while factoring in non-linear degradation.
            </p>

            <div className="simulator-card">
              <div className="simulator-controls">
                <div className="control-group">
                  <label className="control-label">Select Load Type:</label>
                  <div className="appliance-selector">
                    <button
                      className={`selector-btn ${appliance === 'refrigerator' ? 'active' : ''}`}
                      onClick={() => setAppliance('refrigerator')}
                    >
                      Refrigerator (Non-Linear)
                    </button>
                    <button
                      className={`selector-btn ${appliance === 'fan' ? 'active' : ''}`}
                      onClick={() => setAppliance('fan')}
                    >
                      Ceiling Fan (Linear Load)
                    </button>
                    <button
                      className={`selector-btn ${appliance === 'ac' ? 'active' : ''}`}
                      onClick={() => setAppliance('ac')}
                    >
                      Air Conditioner (Compressor)
                    </button>
                  </div>
                </div>

                <div className="control-group slider-group">
                  <div className="slider-header">
                    <label className="control-label">Appliance Age:</label>
                    <span className="slider-val">{age} Years Old</span>
                  </div>
                  <input
                    type="range"
                    min="1"
                    max="12"
                    value={age}
                    onChange={(e) => setAge(parseInt(e.target.value))}
                    className="claude-slider"
                  />
                </div>

                <div className="control-group slider-group">
                  <div className="slider-header">
                    <label className="control-label">Daily Operating Hours:</label>
                    <span className="slider-val">{hours} Hours / Day</span>
                  </div>
                  <input
                    type="range"
                    min="1"
                    max="24"
                    value={hours}
                    onChange={(e) => setHours(parseInt(e.target.value))}
                    className="claude-slider"
                  />
                </div>
              </div>

              {/* Comparison Output */}
              <div className="simulator-results-matrix">
                <div className="result-col static">
                  <span className="res-badge">Static Physics</span>
                  <span className="res-kwh">{staticKwh} kWh</span>
                  <span className="res-note">Assumes 0% aging degradation</span>
                </div>

                <div className="result-col ai">
                  <span className="res-badge">Pure Neural Net</span>
                  <span className="res-kwh">{pureAiKwh} kWh</span>
                  <span className="res-note">
                    {isLinear ? '⚠️ Hallucinating ±30% variance' : 'Underestimates degradation'}
                  </span>
                </div>

                <div className="result-col hybrid">
                  <span className="res-badge highlight">SmartWatt Hybrid</span>
                  <span className="res-kwh highlight">{hybridKwh} kWh</span>
                  <span className="res-note highlight">
                    {isLinear ? '✓ Exact physics gate enforced' : `✓ Includes +${((agingFactor - 1) * 100).toFixed(0)}% aging degradation`}
                  </span>
                </div>
              </div>
            </div>
          </section>

          {/* Section 3: Architecture Proof */}
          <section className="cs-section">
            <span className="cs-sec-num">03 / System Pipeline</span>
            <h2 className="cs-sec-title">Dual-Inference Architecture Workflow</h2>

            <div className="architecture-flow-grid">
              <div className="arch-step-card">
                <span className="step-num">Step 01</span>
                <h4 className="step-title">Wizard Ingestion</h4>
                <p className="step-desc">Next.js 14 structured flow captures age, star-rating, and operational ground truth.</p>
              </div>
              <div className="arch-step-card">
                <span className="step-num">Step 02</span>
                <h4 className="step-title">Dynamic Route Gate</h4>
                <p className="step-desc">FastAPI engine classifies loads into deterministic linear vs non-linear inductive.</p>
              </div>
              <div className="arch-step-card">
                <span className="step-num">Step 03</span>
                <h4 className="step-title">Dual Inference</h4>
                <p className="step-desc">Physics constraint gate for fans; multi-output TensorFlow net for compressor cycles.</p>
              </div>
              <div className="arch-step-card">
                <span className="step-num">Step 04</span>
                <h4 className="step-title">Tariff Integration</h4>
                <p className="step-desc">Persisted to Supabase and evaluated against KSEB telescopic billing brackets.</p>
              </div>
            </div>
          </section>

          {/* Section 4: Quantitative Verification */}
          <section className="cs-section">
            <span className="cs-sec-num">04 / Verification</span>
            <h2 className="cs-sec-title">Benchmark Results Summary</h2>

            <div className="verification-table-wrap">
              <table className="verification-table">
                <thead>
                  <tr>
                    <th>Appliance Category</th>
                    <th>Pure AI Model</th>
                    <th>Static Physics</th>
                    <th>SmartWatt Hybrid</th>
                    <th>Empirical Gain</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td><strong>Refrigerator (Aging)</strong></td>
                    <td>80.3%</td>
                    <td>78.1%</td>
                    <td><strong>94.5%</strong></td>
                    <td className="gain-green">+14.2%</td>
                  </tr>
                  <tr>
                    <td><strong>Ceiling Fan (Linear)</strong></td>
                    <td>31.0%</td>
                    <td>98.0%</td>
                    <td><strong>98.0%</strong></td>
                    <td className="gain-green">+67.0%</td>
                  </tr>
                  <tr>
                    <td><strong>LED Lighting (Linear)</strong></td>
                    <td>20.1%</td>
                    <td>86.0%</td>
                    <td><strong>86.0%</strong></td>
                    <td className="gain-green">+65.9%</td>
                  </tr>
                  <tr>
                    <td><strong>Inference Latency</strong></td>
                    <td>45ms</td>
                    <td>&lt;2ms</td>
                    <td><strong>&lt;20ms</strong></td>
                    <td className="gain-green">Sub-second real-time</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </section>

          {/* Whitepaper Footer CTA */}
          <div className="cs-conclusion-cta">
            <h3 className="cta-heading">Review the Source Code & Documentation</h3>
            <p className="cta-dek">
              The full repository, mathematical derivations, and dataset pipelines are accessible on GitHub.
            </p>
            <div className="cs-cta-actions">
              <a
                href="https://github.com/JishnuPG-tech/SmartWatt"
                target="_blank"
                rel="noopener noreferrer"
                className="btn btn-primary"
              >
                <FiGithub size={16} />
                <span>Open Repository</span>
              </a>
              <Link to="/#contact" className="btn btn-secondary">
                <span>Contact Project Author</span>
                <FiExternalLink size={15} />
              </Link>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default CaseStudy;
